import React, { useState, useEffect } from "react";
import Input from "../utilComponents/Input";
import Button from "../../components/utilComponents/Button";
import CustomDropdown from "../utilComponents/CustomDropdown";
import ConfirmationDialog from "../layout/ConfirmationDialog";
import STATES from "../../constants/states";
import Success from "../message/Success";
import Error from "../message/Error";
import {
  useGetAddressesQuery,
  useAddAddressMutation,
  useUpdateAddressMutation,
  useDeleteAddressMutation,
} from "../../slices/addressSlice";

const ManageAddresses = ({ onClose }) => {
  const [editAddress, setEditAddress] = useState(null);
  const [newAddress, setNewAddress] = useState({
    state: "",
    city: "",
    address: "",
    postcode: "",
  });

  const [isConfirmVisible, setIsConfirmVisible] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const { data: addresses, isLoading, refetch } = useGetAddressesQuery();
  const [addAddress, { isLoading: isAdding }] = useAddAddressMutation();
  const [updateAddress, { isLoading: isUpdating }] = useUpdateAddressMutation();
  const [deleteAddress, { isLoading: isDeleting }] = useDeleteAddressMutation();

  useEffect(() => {
    setNewAddress({
      state: "",
      city: "",
      address: "",
      postcode: "",
    });
    setEditAddress(null);
  }, []);

  const handleAddOrEditAddress = async () => {
    if (
      !newAddress.state ||
      !newAddress.city ||
      !newAddress.address ||
      !newAddress.postcode
    ) {
      setErrorMessage("All fields are required.");
      return;
    }

    try {
      if (editAddress) {
        // Update existing address
        await updateAddress({
          id: editAddress._id,
          state: newAddress.state,
          city: newAddress.city,
          address: newAddress.address,
          postcode: newAddress.postcode,
        }).unwrap();
        setSuccessMessage("Address updated successfully!");
      } else {
        // Add a new address
        await addAddress(newAddress).unwrap();
        setSuccessMessage("Address added successfully!");
      }

      await refetch(); // Refresh the address list
      setEditAddress(null); // Exit edit mode
      setNewAddress({ state: "", city: "", address: "", postcode: "" }); // Clear form
      onClose(); // Close modal
    } catch (error) {
      console.error("Error saving address:", error);
      setErrorMessage("Failed to save address. Please try again.");
    }
  };

  const handleDeleteAddress = async () => {
    try {
      if (addressToDelete) {
        await deleteAddress(addressToDelete._id);
        setAddressToDelete(null);
        await refetch();
        setIsConfirmVisible(false);
        setSuccessMessage("Address deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      setErrorMessage("Failed to delete address. Please try again.");
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg w-full md:w-auto">
      <h2 className="text-lg font-semibold font-nunito text-agBlack mb-4">
        Manage Addresses
      </h2>
      <hr className="mb-4 border-agPrimaryLight" />

      {isLoading ? (
        <p>Loading address...</p>
      ) : addresses?.length > 0 && !editAddress ? (
        // Render the list of addresses
        <div className="grid grid-cols-1 gap-4">
          {addresses.map((address) => (
            <div
              key={address._id}
              className="w-full p-4 rounded-md flex flex-col md:flex-row md:space-x-8 justify-between items-left"
            >
              <div className="flex flex-col space-y-4 md:flex md:flex-row md:space-x-8">
                <div className="flex flex-col space-y-4">
                  <div>
                    <p className="text-sm font-medium text-agLightGray">
                      State
                    </p>
                    <p className="text-xs font-medium text-agBlack">
                      {address.state}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-agLightGray">
                      Address
                    </p>
                    <p className="text-xs font-medium text-agBlack w-56">
                      {address.address}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col space-y-4">
                  <div>
                    <p className="text-sm text-agLightGray">City</p>
                    <p className="text-xs font-medium text-agBlack">
                      {address.city}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-agLightGray w-24">
                      Post Code
                    </p>
                    <p className="text-xs font-medium text-agBlack">
                      {address.postcode}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex md:flex-col md:space-y-14 md:items-left md:justify-left items-center space-x-12 justify-between md:justify-normal w-full lg:w-26 mt-4 md:mt-0">
                <button
                  onClick={() => {
                    setEditAddress(address); // Enter edit mode
                    setNewAddress(address); // Pre-fill form
                  }}
                  className="text-agBlue"
                >
                  <span className="material-icons-round text-sm">edit</span>
                  <span className="text-sm">Edit</span>
                </button>
                <button
                  className="text-agRedBg hover:text-agRed cursor-pointer flex items-center space-x-1"
                  onClick={() => {
                    setAddressToDelete(address);
                    setIsConfirmVisible(true);
                  }}
                >
                  <span className="material-icons-round text-sm">delete</span>
                  <span className="text-sm">Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Render the form for adding/editing addresses
        <div>
          <CustomDropdown
            options={STATES}
            value={newAddress.state}
            onChange={(selected) =>
              setNewAddress({ ...newAddress, state: selected })
            }
            placeholder="Select a state"
          />
          <p className="my-4"></p>
          <Input
            value={newAddress.city}
            onChange={(e) =>
              setNewAddress({ ...newAddress, city: e.target.value })
            }
            placeholder="Enter city"
          />
          <Input
            value={newAddress.address}
            onChange={(e) =>
              setNewAddress({ ...newAddress, address: e.target.value })
            }
            placeholder="Enter address"
          />
          <Input
            type="number"
            value={newAddress.postcode}
            onChange={(e) =>
              setNewAddress({ ...newAddress, postcode: e.target.value })
            }
            placeholder="Enter postcode"
          />
          <div className="mt-6 flex space-x-4">
            <Button
              variant="primary"
              onClick={handleAddOrEditAddress}
              isLoading={isAdding || isUpdating}
              fullWidth
            >
              {editAddress ? "SAVE" : "ADD ADDRESS"}
            </Button>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                setEditAddress(null);
                setNewAddress({
                  state: "",
                  city: "",
                  address: "",
                  postcode: "",
                });
              }}
            >
              CANCEL
            </Button>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isVisible={isConfirmVisible}
        onClose={() => setIsConfirmVisible(false)}
        onConfirm={handleDeleteAddress}
        message={`Are you sure you want to delete this address?`}
        confirmText="DELETE"
        cancelText="CANCEL"
        loading={isDeleting}
      />

      {/* Success and Error Messages */}
      {successMessage && (
        <Success
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <Error message={errorMessage} onClose={() => setErrorMessage("")} />
      )}
    </div>
  );
};

export default ManageAddresses;
